/**
 * Responsive candidates for an image the gateway serves.
 *
 * `/upload/file/<key>` answers `?w=640|1080|1920` with a WebP resized to that width (built on
 * first request, cached), so a phone can take the 1080 copy instead of the 2560 delivery
 * copy. Any other host (a stock-photo URL, a feed CDN) gets no srcset — one src, as before.
 */
export const RESPONSIVE_WIDTHS = [640, 1080, 1920] as const;
export const FULL_WIDTH = 2560;

export function isGatewayUploadUrl(url: string): boolean {
  return /^https?:\/\/[^/]+\/upload\/file\/[^?#]+\.(?:jpe?g|png|webp)(?:$|\?)/i.test(url);
}

export function widthUrl(url: string, width: number): string {
  const [base, query = ''] = url.split('?', 2);
  const params = new URLSearchParams(query);
  params.set('w', String(width));
  return `${base}?${params.toString()}`;
}

export interface ResponsiveImage {
  src: string;
  srcSet?: string;
  sizes?: string;
}

/** Full-bleed image (hero): every width, sized to the viewport. */
export function heroImage(url: string): ResponsiveImage {
  if (!isGatewayUploadUrl(url)) return { src: url };
  const candidates = RESPONSIVE_WIDTHS.map((w) => `${widthUrl(url, w)} ${w}w`);
  candidates.push(`${url} ${FULL_WIDTH}w`);
  return { src: widthUrl(url, 1080), srcSet: candidates.join(', '), sizes: '100vw' };
}

/** A card photo never renders wider than ~640 css px: one small copy. */
export function cardImage(url: string): string {
  return isGatewayUploadUrl(url) ? widthUrl(url, 640) : url;
}
